import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    CircularProgress,
    Grid,
    InputAdornment,
    ListItemText,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import SensorsOffOutlinedIcon from "@mui/icons-material/SensorsOffOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import _ from "lodash";
import safetyLimitServices from "app/services/automations/safety-limit-services";
import { actuatorLabel, formatLoopValue, variableWithArticle } from "app/shared/automations/controlLoopDisplay";
import { errorMessage } from "app/shared/devices/actuatorDisplay";
import AutomationMetaHeader from "../../components/AutomationMetaHeader";
import EditorSaveBar from "../../components/EditorSaveBar";
import RecipientsEditor from "../../components/RecipientsEditor";
import SectionCard from "../../components/SectionCard";

export const OPERATOR_OPTIONS = [
    { value: "above", label: "Llega a o supera" },
    { value: "below", label: "Llega a o baja de" },
];

const SAFE_STATE_WORDS = { OFF: "apagada", ON: "encendida" };

const EMPTY_FORM = {
    id: null,
    name: "",
    description: "",
    notification_group: "",
    enabled: true,
    sensor_device_id: "",
    measurement: "",
    operator: "above",
    threshold: "",
    trip_when_stale: true,
    stale_after_minutes: 15,
    actuator_device_ids: [],
    fail_safe_states: {},
    recipients: [],
};

const valueOrBlank = (value) => (_.isNil(value) ? "" : value);

const limitToForm = (limit = {}) => ({
    ...EMPTY_FORM,
    id: limit.id || null,
    name: limit.name || "",
    description: limit.description || "",
    notification_group: limit.notification_group || "",
    enabled: limit.enabled !== false,
    sensor_device_id: valueOrBlank(limit.sensor_device_id),
    measurement: limit.measurement || "",
    operator: limit.operator || "above",
    threshold: valueOrBlank(limit.threshold),
    trip_when_stale: limit.trip_when_stale !== false,
    stale_after_minutes: valueOrBlank(limit.stale_after_minutes) || 15,
    actuator_device_ids: limit.actuator_device_ids || [],
    fail_safe_states: limit.fail_safe_states || {},
    recipients: (limit.recipients || []).map((recipient) => _.pick(recipient, ["id", "user_id", "channel", "enabled"])),
});

const draftPayload = (message) => {
    const payload = message?.draft_payload || {};
    return { payload, limit: payload.safety_limit || payload };
};

// Editor de un límite de seguridad: cuándo se activa, qué salidas protege, qué hacer si el sensor calla y a quién
// avisar. Mientras está activo, sus salidas quedan en el estado seguro y nadie puede encenderlas.
const SafetyLimitEditor = ({ id, duplicateFromId, query = {}, assistantDraft, groupOptions = [], onSaved, onCancel, setAssistantContext }) => {
    const [options, setOptions] = useState(null);
    const [form, setForm] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    // Al guardar se vuelve a la lista: el editor ya no existe cuando termina la petición.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);
    const draftTargetId = assistantDraft ? (draftPayload(assistantDraft).payload.mode === "update" ? draftPayload(assistantDraft).payload.target_id : null) : null;
    const editingId = id || draftTargetId || null;

    useEffect(() => {
        let active = true;
        const sourceId = editingId || duplicateFromId;

        Promise.all([
            safetyLimitServices.getEditOptions(),
            sourceId ? safetyLimitServices.getSafetyLimit(sourceId) : Promise.resolve(null),
        ])
            .then(([editOptions, limit]) => {
                if (!active) return;
                setOptions(editOptions || {});

                if (assistantDraft) {
                    const { limit: draft } = draftPayload(assistantDraft);
                    const fromDraft = _.omitBy(limitToForm(draft), (value, key) => key === "id" || value === "" || _.isNil(value));
                    setForm({ ...limitToForm(limit || {}), ...fromDraft, id: limit?.id || null });
                } else if (id && limit) {
                    setForm(limitToForm(limit));
                } else if (duplicateFromId && limit) {
                    setForm({ ...limitToForm(limit), id: null, name: `Copia de ${limit.name}`, enabled: false });
                } else {
                    setForm({ ...EMPTY_FORM, actuator_device_ids: query.actuator_id ? [Number(query.actuator_id)] : [] });
                }
            })
            .catch(() => { if (active) setLoadError("No se pudo cargar el límite."); });

        return () => { active = false; };
    }, [assistantDraft, duplicateFromId, editingId, id, query.actuator_id]);

    useEffect(() => {
        if (!form) return;
        setAssistantContext?.({
            mode: "form",
            current_safety_limit: _.pick(form, ["id", "name", "sensor_device_id", "measurement", "operator", "threshold", "actuator_device_ids", "enabled"]),
        });
    }, [form, setAssistantContext]);

    const actuators = useMemo(() => options?.actuators || [], [options]);
    const sensors = useMemo(() => options?.sensors || [], [options]);
    const stale = options?.stale_after_minutes || { min: 5, max: 120, default: 15 };
    const selectedSensor = _.find(sensors, (sensor) => `${sensor.id}` === `${form?.sensor_device_id}`);
    const selectedVariable = _.find(selectedSensor?.variables, { field: form?.measurement });
    const unit = selectedVariable?.unit;
    const selectedOutputs = _.filter(actuators, (actuator) => _.includes(form?.actuator_device_ids, actuator.id));
    const outputsWithoutSafeState = _.filter(selectedOutputs, (actuator) => !actuator.supervised);
    const staleMinutes = Number(form?.stale_after_minutes);
    const staleError = form && (!Number.isFinite(staleMinutes) || staleMinutes < stale.min || staleMinutes > stale.max)
        ? `Entre ${stale.min} y ${stale.max} minutos.`
        : null;

    const update = (patch) => setForm((current) => ({ ...current, ...patch }));
    const field = (name) => (event) => update({ [name]: event.target.value });

    const canSave = !!form
        && !!form.sensor_device_id
        && !!form.measurement
        && form.threshold !== "" && Number.isFinite(Number(form.threshold))
        && selectedOutputs.length > 0
        && _.every(outputsWithoutSafeState, (actuator) => !!form.fail_safe_states?.[actuator.id])
        && !staleError;

    const save = () => {
        if (!canSave) return;
        const payload = {
            name: form.name?.trim() || "",
            description: form.description || "",
            notification_group: form.notification_group?.trim() || null,
            enabled: form.enabled !== false,
            sensor_device_id: form.sensor_device_id,
            measurement: form.measurement,
            operator: form.operator,
            threshold: Number(form.threshold),
            trip_when_stale: form.trip_when_stale,
            stale_after_minutes: staleMinutes,
            actuator_device_ids: form.actuator_device_ids,
            fail_safe_states: _.pick(form.fail_safe_states, outputsWithoutSafeState.map((actuator) => `${actuator.id}`)),
            recipients: (form.recipients || []).filter((recipient) => recipient.user_id)
                .map((recipient) => _.pick(recipient, ["user_id", "channel", "enabled"])),
        };

        setSaving(true);
        setSaveError(null);
        const request = form.id
            ? safetyLimitServices.updateSafetyLimit(form.id, payload)
            : safetyLimitServices.createSafetyLimit(payload);
        request
            .then((saved) => onSaved?.(saved))
            .catch((error) => { if (mountedRef.current) setSaveError(errorMessage(error, "No se pudo guardar el límite.")); })
            .finally(() => { if (mountedRef.current) setSaving(false); });
    };

    if (loadError) return <Alert severity="error">{loadError}</Alert>;
    if (!form || !options) {
        return (
            <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const variableName = variableWithArticle(selectedVariable);
    const condition = form.threshold !== "" && selectedVariable
        ? `Se activa cuando ${variableName} de «${selectedSensor.name}» llega a ${formatLoopValue(form.threshold, unit)} o ${form.operator === "above" ? "más" : "menos"}.`
        : "La condición que activa el límite.";

    return (
        <>
            <Stack spacing={2.25}>
                <AutomationMetaHeader
                    automation={form}
                    onChange={update}
                    onBack={onCancel}
                    groupOptions={groupOptions}
                    namePlaceholder="Nombre del límite (opcional)"
                />

                <SectionCard title="Cuándo se activa" subtitle={condition} icon={<ShieldOutlinedIcon />} accent="error.main">
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                label="Sensor"
                                value={form.sensor_device_id}
                                onChange={(event) => update({ sensor_device_id: event.target.value, measurement: "" })}
                                helperText={sensors.length ? undefined : "No hay sensores con mediciones numéricas."}
                                SelectProps={{ renderValue: () => selectedSensor?.name || "" }}>
                                {sensors.map((sensor) => (
                                    <MenuItem key={`sensor-${sensor.id}`} value={sensor.id}>
                                        <ListItemText primary={sensor.name} secondary={sensor.store_name} />
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField select fullWidth label="Medición" value={form.measurement} onChange={field("measurement")} disabled={!selectedSensor}>
                                {(selectedSensor?.variables || []).map((variable) => (
                                    <MenuItem key={`variable-${variable.field}`} value={variable.field}>
                                        {variable.label}{variable.unit ? ` (${variable.unit})` : ""}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={7} md={3}>
                            <TextField select fullWidth label="Cuando" value={form.operator} onChange={field("operator")}>
                                {OPERATOR_OPTIONS.map((option) => (
                                    <MenuItem key={`operator-${option.value}`} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={5} md={2}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Valor"
                                value={form.threshold}
                                onChange={field("threshold")}
                                InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
                            />
                        </Grid>
                    </Grid>
                </SectionCard>

                <SectionCard
                    title="Qué protege"
                    subtitle="Mientras el límite esté activo, estas salidas quedan en su estado seguro y nadie puede encenderlas: ni un control, ni una regla, ni a mano. Cuando la medición vuelve a la normalidad, se liberan solas."
                    icon={<PowerSettingsNewOutlinedIcon />}
                    accent="warning.main">
                    <Autocomplete
                        multiple
                        options={actuators}
                        value={selectedOutputs}
                        onChange={(_event, selected) => update({ actuator_device_ids: _.map(selected, "id") })}
                        getOptionLabel={actuatorLabel}
                        getOptionDisabled={(actuator) => !actuator.available}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderOption={(props, actuator) => (
                            <li {...props} key={`output-${actuator.id}`}>
                                <ListItemText
                                    primary={actuatorLabel(actuator)}
                                    secondary={actuator.available
                                        ? _.compact([actuator.store_name, actuator.controlled_by && `la maneja «${actuator.controlled_by.name}»`]).join(" · ")
                                        : actuator.unavailable_reason}
                                />
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Salidas" placeholder="Agregar salida" />}
                    />
                    {outputsWithoutSafeState.map((actuator) => (
                        <TextField
                            key={`fail-safe-${actuator.id}`}
                            select
                            required
                            label={`Mientras esté activo, «${actuator.name}» debe…`}
                            value={form.fail_safe_states?.[actuator.id] || ""}
                            onChange={(event) => update({ fail_safe_states: { ...form.fail_safe_states, [actuator.id]: event.target.value } })}
                            helperText="Queda como su estado seguro: también es lo que hace si la plataforma deja de responder."
                            sx={{ maxWidth: { md: 520 } }}>
                            {(options.fail_safe_states || ["OFF", "ON"]).map((state) => (
                                <MenuItem key={`fail-safe-${actuator.id}-${state}`} value={state}>{`Quedar ${SAFE_STATE_WORDS[state] || state}`}</MenuItem>
                            ))}
                        </TextField>
                    ))}
                    {selectedOutputs.filter((actuator) => actuator.supervised).map((actuator) => (
                        <Typography key={`safe-${actuator.id}`} variant="body2" color="text.secondary">
                            «{actuator.name}» queda <b>{SAFE_STATE_WORDS[actuator.fail_safe_state]}</b> (su estado seguro).
                        </Typography>
                    ))}
                </SectionCard>

                <SectionCard
                    title="Si el sensor deja de informar"
                    subtitle="Sin datos no se puede saber si la medición está bien."
                    icon={<SensorsOffOutlinedIcon />}
                    accent="primary.main">
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                select
                                fullWidth
                                label="Qué hacer"
                                value={form.trip_when_stale ? "trip" : "ignore"}
                                onChange={(event) => update({ trip_when_stale: event.target.value === "trip" })}>
                                <MenuItem value="trip">Activar el límite (lo más seguro)</MenuItem>
                                <MenuItem value="ignore">No hacer nada hasta que vuelvan los datos</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Después de"
                                value={form.stale_after_minutes}
                                onChange={field("stale_after_minutes")}
                                error={!!staleError}
                                helperText={staleError || "Unas 3 veces el intervalo del sensor."}
                                inputProps={{ min: stale.min, max: stale.max }}
                                InputProps={{ endAdornment: <InputAdornment position="end">min sin datos</InputAdornment> }}
                            />
                        </Grid>
                    </Grid>
                </SectionCard>

                <SectionCard
                    title="A quién avisar"
                    subtitle="Cuando el límite se activa y cuando vuelve a la normalidad."
                    icon={<NotificationsActiveOutlinedIcon />}
                    accent="primary.main">
                    <RecipientsEditor
                        recipients={form.recipients}
                        onChange={(recipients) => update({ recipients })}
                        users={options.users || []}
                        channels={options.channels || []}
                    />
                </SectionCard>

                {!!saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
                {!canSave && (
                    <Alert severity="info">
                        Elige el sensor, la medición y el valor, y al menos una salida{outputsWithoutSafeState.length ? " con lo que debe hacer mientras el límite esté activo" : ""} para guardar el límite.
                    </Alert>
                )}
            </Stack>
            <EditorSaveBar saving={saving} disabled={!canSave} onSave={save} label="Guardar límite" />
        </>
    );
};

export default SafetyLimitEditor;
